import { Component, effect, inject, OnInit, signal } from '@angular/core';
import { User } from '@angular/fire/auth';
import { AuthService } from '../../services/auth/auth.service';
import { Router } from '@angular/router';
import { StorageService } from '../../services/storage/storage.service';
import { TranslateService } from '@ngx-translate/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastService } from '../../services/toast.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorService } from '../../../../shared/services/error.service';
import { AuthApiService } from '../../services/auth-api.service';
import { AlertController } from '@ionic/angular';
import { ConsentService, UserConsent } from '../../services/consent.service';
import { LoggerService } from '../../../../shared/services/logger.service';
import { NotificationsService } from '../../../../shared/services/notifications.service';

@Component({
  selector: 'app-settings',
  templateUrl: './settings.page.html',
  styleUrls: ['./settings.page.scss'],
  standalone: false,
})
export class SettingsPage implements OnInit {
  authService = inject(AuthService);
  authApiService = inject(AuthApiService);
  router = inject(Router);
  storageService = inject(StorageService);
  translateService = inject(TranslateService);
  alertController = inject(AlertController);
  consentService = inject(ConsentService);
  loggerService = inject(LoggerService);
  notificationsService = inject(NotificationsService);

  user = signal<User | null>(null);
  userConsent = signal<UserConsent | null>(null);

  // Use a string for selectedLanguage for template compatibility
  selectedLanguage: string;

  toastService = inject(ToastService);
  errorService = inject(ErrorService);
  profileForm: FormGroup;

  isButtonDisabled = signal<boolean>(true);

  // Add loading and error states
  loading = false;
  error: string | null = null;
  showSuccessState = signal<boolean>(false);

  constructor(private fb: FormBuilder) {
    this.selectedLanguage = this.translateService.currentLang;
    effect(() => {
      this.useLanguage(this.selectedLanguage);
    });
    this.profileForm = this.fb.group({
      phone: ['', [Validators.required, Validators.pattern(/^995-5\d{2}-\d{3}-\d{3}$/)]],
    });
  }

  async ngOnInit() {
    this.user.set(await this.authService.tryGetUser());
    this.profileForm.valueChanges.subscribe(() => {
      this.isButtonDisabled.set(false);
    });

    // Load consent data
    await this.loadConsentData();

    this.loading = true;
    this.authApiService.getMobilePhone().subscribe(
      (response) => {
        if (response.mobilePhone != null) {
          let formatedMobileNumber = response.mobilePhone.match(/.{1,3}/g)?.join('-') || '';
          this.profileForm.patchValue({ phone: formatedMobileNumber });
          this.isButtonDisabled.set(true);
        }
        this.loading = false;
      },
      (err: HttpErrorResponse) => {
        this.errorService.handleError(err);
        this.error = err.message;
        this.loading = false;
      }
    );
  }

  private async loadConsentData() {
    try {
      const consent = await this.consentService.getUserConsent();

      this.userConsent.set(consent);
    } catch (error) {
      this.loggerService.logException(error, 'Error loading consent data');
    }
  }

  async manageConsent() {
    // Navigate to consent page to manage settings
    this.router.navigate(['/consent']);
  }

  onSubmit() {
    if (this.profileForm.valid) {
      this.isButtonDisabled.set(true);

      const phoneValue = this.profileForm.value.phone.replace(/\D/g, '');

      this.loading = true;
      this.authApiService.updateMobilePhone({ mobilePhone: phoneValue }).subscribe({
        next: () => {
          this.toastService.success(this.translateService.instant('PROFILE.PHONE_SUCCESSFULLY_CHANGED'));

          this.loggerService.logEvent('profile_phone_changed', { phone: phoneValue });

          this.authService.changeUserPhone(phoneValue);
          this.loading = false;
          this.showSuccessState.set(true);
        },
        error: (err: HttpErrorResponse) => {
          this.errorService.handleError(err);
          this.error = err.message;
          this.loading = false;
        },
      });
    }
  }

  continueToHome() {
    this.router.navigate(['/home']);
  }

  get phone() {
    return this.profileForm.get('phone');
  }

  formatPhoneNumber(event: any) {
    let input = event.target.value.replace(/\D/g, ''); // Remove non-numeric characters

    // Limit to 12 digits (995-555-555-555)
    input = input.substring(0, 12);

    // Insert "-" immediately after every 3 digits
    let formattedPhone = input.match(/.{1,3}/g)?.join('-') || '';

    // Update the form control value
    this.phone?.setValue(formattedPhone, { emitEvent: false });
  }

  useLanguage(language: string): void {
    this.translateService.use(language);
    this.storageService.save('lang', language);
    this.selectedLanguage = language;
  }

  async confirmDeleteAccount() {
    const alert = await this.alertController.create({
      header: this.translateService.instant('CONFIRMATION.DIALOG.TITLE'),
      message: this.translateService.instant('SETTINGS.DELETE_ACCOUNT.CONFIRMATION'),
      buttons: [
        {
          text: this.translateService.instant('CONFIRMATION.DIALOG.NO'),
          role: 'cancel',
          handler: () => {},
        },
        {
          text: this.translateService.instant('CONFIRMATION.DIALOG.YES'),
          handler: () => {
            this.deleteAccount();
          },
        },
      ],
    });

    await alert.present();
  }

  deleteAccount() {
    this.loading = true;
    this.authApiService.deleteAccount().subscribe({
      next: () => {
        this.authService.signOut();
        this.loading = false;
      },
      error: (err: HttpErrorResponse) => {
        this.errorService.handleError(err);
        this.error = err.message;
        this.loading = false;
      },
    });
  }

  async confirmLogout() {
    const alert = await this.alertController.create({
      header: this.translateService.instant('CONFIRMATION.DIALOG.TITLE'),
      message: this.translateService.instant('CONFIRMATION.DIALOG.LOGOUT'),
      buttons: [
        {
          text: this.translateService.instant('CONFIRMATION.DIALOG.NO'),
          role: 'cancel',
          handler: () => {},
        },
        {
          text: this.translateService.instant('CONFIRMATION.DIALOG.YES'),
          handler: async () => {
            await this.authService.signOut();
          },
        },
      ],
    });
    await alert.present();
  }

  async turnOnNotifications() {
    const isAllowed = await this.notificationsService.ensureNotificationsAllowed();

    if (!isAllowed) {
      this.toastService.info(this.translateService.instant('NOTIFICATIONS.NOT_ALLOWED'), 5000);
    }
    else {
      this.toastService.success(this.translateService.instant('NOTIFICATIONS.TURNED_ON'));
    }
  }
}
